/**
 * Cita del historial al responder/reenviar, con el mismo formato que Outlook
 * pega en sus respuestas: línea gris fina arriba y bloque De/Enviado el/Para/
 * CC/Asunto en negrita. Antes se usaba una barra lateral gris que no se veía
 * como el resto del hilo en Outlook.
 *
 * Sin imports de `./email`, para evitar un ciclo (email.ts usa QUOTE_CLASS).
 */

export const QUOTE_CLASS = 'kila-quote'
export const QUOTE_OPEN_TAG = '<div class="kila-quote">'
/** Cita anterior (borradores guardados antes de este cambio) */
export const LEGACY_QUOTE_MARKER = '<br><br><div style="border-left: 2px solid #ccc;'

export interface QuoteAddress {
  name?: string
  email: string
}

export interface QuoteHeader {
  from: string
  sent: string
  to: string
  cc: string
  subject: string
}

export interface QuoteLabels {
  from: string
  sent: string
  to: string
  cc: string
  subject: string
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** `Nombre <correo>` si hay nombre distinto del correo; si no, solo el correo. */
export function formatQuoteAddress(addr: QuoteAddress): string {
  const email = (addr.email || '').trim()
  const name = (addr.name || '').trim()
  if (!email) return name || ''
  if (name && name.toLowerCase() !== email.toLowerCase()) return `${name} <${email}>`
  return email
}

/** Lista de direcciones formateadas, sin las vacías, unidas con `; `. */
export function formatQuoteAddressList(list: QuoteAddress[]): string {
  return list.map(formatQuoteAddress).filter(addr => addr !== '').join('; ')
}

/**
 * Fecha/hora local como la cita Outlook. Sin fecha válida devuelve ''. La
 * hora se arma a mano, sin `toLocaleTimeString`, para controlar el 12 horas y
 * el sufijo (AM/PM en inglés, "a. m."/"p. m." en español) exactamente igual
 * que Outlook.
 */
export function formatQuoteDate(date: Date | null | undefined, locale: string): string {
  if (!date || isNaN(date.getTime())) return ''

  const hours24 = date.getHours()
  const minutes = pad2(date.getMinutes())
  const isPM = hours24 >= 12
  let h12 = hours24 % 12
  if (h12 === 0) h12 = 12

  if (locale.toLowerCase().startsWith('en')) {
    const datePart = new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date)
    return `${datePart} ${h12}:${minutes} ${isPM ? 'PM' : 'AM'}`
  }

  const datePart = new Intl.DateTimeFormat('es-CO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)
  return `${datePart} ${pad2(h12)}:${minutes} ${isPM ? 'p. m.' : 'a. m.'}`
}

/**
 * Cita citada estilo Outlook: línea gris fina y bloque De/Enviado el/Para/CC/
 * Asunto en negrita. `bodyHtml` se inserta tal cual, porque el llamador ya lo
 * sanitizó.
 */
export function buildQuotedMessage(header: QuoteHeader, labels: QuoteLabels, bodyHtml: string): string {
  const cc =
    header.cc.trim() !== ''
      ? `<br><b>${escapeHtml(labels.cc)}</b> ${escapeHtml(header.cc)}`
      : ''

  return (
    `${QUOTE_OPEN_TAG}<div><br></div>` +
    `<div style="border:none;border-top:solid #E1E1E1 1.0pt;padding:3.0pt 0cm 0cm 0cm">` +
    `<p style="margin:0cm;font-family:Calibri,Arial,Helvetica,sans-serif;font-size:11.0pt">` +
    `<b>${escapeHtml(labels.from)}</b> ${escapeHtml(header.from)}<br>` +
    `<b>${escapeHtml(labels.sent)}</b> ${escapeHtml(header.sent)}<br>` +
    `<b>${escapeHtml(labels.to)}</b> ${escapeHtml(header.to)}${cc}<br>` +
    `<b>${escapeHtml(labels.subject)}</b> ${escapeHtml(header.subject)}` +
    `</p></div><div><br></div>${bodyHtml}</div>`
  )
}

/**
 * Separa el cuerpo nuevo de la cita anterior, reconociendo tanto la cita
 * nueva (`QUOTE_OPEN_TAG`) como la vieja de borradores anteriores a este
 * cambio (`LEGACY_QUOTE_MARKER`). Si hay dos citas anidadas, corta en la
 * primera.
 */
export function splitAtQuote(html: string): { before: string; quote: string } {
  const idxNew = html.indexOf(QUOTE_OPEN_TAG)
  const idxLegacy = html.indexOf(LEGACY_QUOTE_MARKER)
  const indices = [idxNew, idxLegacy].filter(i => i >= 0)

  if (indices.length === 0) return { before: html, quote: '' }

  const i = Math.min(...indices)
  return { before: html.slice(0, i), quote: html.slice(i) }
}
